import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import {
  FinancialHeader,
  EventsTimeline,
  RevenueBreakdownPanel,
  ExpenseBreakdownPanel,
  PreStartupDisplay,
  FundingCalculatorHelpModal,
} from "../molecules";
import { TimelineSection, FinancialVisualizationDashboard } from "../organisms";
import {
  calculateBurndownSnapshot,
  calculateAssetMonthlyImpact,
  resolveActiveAssetEvents,
} from "../../utils/chart/burndown.utils";
import { calculateMonthlyCustomerMetrics } from "../../utils/chart/customer-projection.utils";
import { getSnapshotNotificationKey } from "../../utils/chart/snapshot-notification.utils";
import {
  getOrBuildCache,
  getCashTransitions,
  getPrimaryCashZeroInRange,
  getCashZeroEventForDisplay,
  computeRunwayMonths,
  formatCashZeroDate,
} from "../../utils/chart/financial-cache.utils";
import { mapChartDataToStartup } from "../../utils/chart/adapter.utils";
import {
  getTimelineSegmentMarkers,
  getDateProgressInVisibleRange,
} from "../../utils/chart/chart-period.utils";
import {
  calculateExpenseAtMonth,
  calculateRevenueAtMonth,
} from "../../utils/chart/scaling.utils";
import { safeDate } from "../../utils/date";
import { shouldExcludeAcquisitionExpenseFromBurn } from "../../utils/chart/acquisition-expense.utils";
import { CHART_COLORS } from "@/constants/chart-colors";
import { buildRevisionTimelineMarkers } from "../../utils/chart/segment-grouping.utils";
import type { RevenueData } from "../../utils/chart";
import { calculateVisibleDateRange } from "../../utils/chart/timeline-range.utils";
import type {
  ExpenseData,
  TimelineSnapshot,
  BurndownChartProps,
} from "../../types/burndown.types";

const BurndownChart: React.FC<BurndownChartProps> = ({
  data,
  onExpenseClick,
  onRevenueClick,
  onEventClick,
  onCustomerProjectionClick,
  onSubscriptionTierClick,
  onCapacityGroupClick,
  onAddExpense,
  onAddRevenue,
  onAddEvent,
  onIncorporateGrowthPlan,
  onAddCustomerProjection,
  onAddCapacityGroup,
  onRevisionReviseAgain,
  onTimeChange,
  zoomLevel = "18months",
  zoomCenter,
  customDateRange,
  initialCurrentDate,
  rawExpenses = [],
  rawRevenues = [],
  rawEvents = [],
  startupId,
  customers = [],
  subscriptionTiers = [],
  customerGrowthModels = [],
  customerAcquisitionCosts = [],
  capacityGroups = [],
  teamMembers,
  readOnly = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  const onTimeChangeRef = useRef(onTimeChange);
  const dataRef = useRef(data);
  const lastNotifiedSnapshotKeyRef = useRef<string | null>(null);
  const [currentDate, setCurrentDate] = useState<Date>(
    () => initialCurrentDate || zoomCenter || data.startDate
  );
  const [isDragging, setIsDragging] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastUpdateRef = useRef<number>(0);
  const dragDateRef = useRef<Date | null>(null);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [showRevisionMarkers, setShowRevisionMarkers] = useState(true);
  // Separate snapshot for breakdown charts that only updates when scrubbing stops
  const [breakdownSnapshot, setBreakdownSnapshot] =
    useState<TimelineSnapshot | null>(null);

  const visibleDateRange = useMemo(
    () =>
      calculateVisibleDateRange({
        zoomLevel,
        zoomCenter,
        startupStartDate: data.startDate,
        startupEndDate: data.endDate,
        customRange: customDateRange,
      }),
    [zoomLevel, zoomCenter, data.startDate, data.endDate, customDateRange],
  );

  // Update data ref only when data actually changes
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  // Update ref when onTimeChange prop changes
  useEffect(() => {
    onTimeChangeRef.current = onTimeChange;
  }, [onTimeChange]);

  // Sync currentDate with zoomCenter when panning
  useEffect(() => {
    if (zoomCenter) {
      setCurrentDate(zoomCenter);
    }
  }, [zoomCenter]);

  // Set currentDate when initialCurrentDate changes (for "Today" button without centering)
  useEffect(() => {
    if (initialCurrentDate && !zoomCenter) {
      setCurrentDate(initialCurrentDate);
    }
  }, [initialCurrentDate, zoomCenter]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Calculate snapshot at any given date
  const calculateSnapshot = useCallback(
    (targetDate: Date): TimelineSnapshot => {
      // Use the latest data prop directly instead of ref
      const currentData = data;
      // Use utility function to calculate the snapshot
      const snapshot = calculateBurndownSnapshot(currentData, targetDate);
      const cache = getOrBuildCache(mapChartDataToStartup(currentData));
      const runway = computeRunwayMonths(
        cache,
        targetDate,
        snapshot.cashBalance,
        snapshot.netMonthlyChange,
      );

      // Customer metrics for the scrubbed date
      const customerData = currentData.customerProjectionData
        ? calculateMonthlyCustomerMetrics(
            targetDate,
            currentData.customerProjectionData,
          )
        : {
            totalCustomers: 0,
            newCustomers: 0,
            churnedCustomers: 0,
            subscriptionRevenue: 0,
            acquisitionCosts: 0,
            netCustomerChange: 0,
            customersByTier: {},
            averageRevenuePerCustomer: 0,
          };

      return {
        date: snapshot.date,
        cashBalance: snapshot.cashBalance,
        monthlyBurn: snapshot.monthlyBurn,
        monthlyRevenue: snapshot.monthlyRevenue || 0,
        netMonthlyChange: snapshot.netMonthlyChange || -snapshot.monthlyBurn,
        activeExpenses: snapshot.activeExpenses,
        activeRevenues: snapshot.activeRevenues || [],
        runway,
        // Customer metrics
        customerCount: customerData.totalCustomers,
        newCustomers: customerData.newCustomers,
        averageRevenuePerCustomer: customerData.averageRevenuePerCustomer,
        customerRevenue: customerData.subscriptionRevenue,
        customersByTier: customerData.customersByTier,
      };
    },
    [data] // Removed memoizedCustomerData to prevent re-renders
  );

  // Fast cursor position update via direct DOM manipulation
  const updateCursorPosition = useCallback(
    (clientX: number) => {
      if (!timelineRef.current || !cursorRef.current) return null;

      const rect = timelineRef.current.getBoundingClientRect();
      const progress = Math.max(
        0,
        Math.min(1, (clientX - rect.left) / rect.width)
      );

      // Update cursor position directly for instant visual feedback
      cursorRef.current.style.left = `${progress * 100}%`;

      // Calculate the date for this position
      const timeRange =
        visibleDateRange.endDate.getTime() -
        visibleDateRange.startDate.getTime();
      const newDate = new Date(
        visibleDateRange.startDate.getTime() + progress * timeRange
      );

      return newDate;
    },
    [visibleDateRange]
  );

  // Handle timeline scrubbing with throttled updates during drag
  const handleTimelineInteraction = useCallback(
    (clientX: number) => {
      // Fast cursor update with direct DOM manipulation
      const newDate = updateCursorPosition(clientX);
      if (!newDate) return;

      // Store current drag date for throttled updates
      dragDateRef.current = newDate;

      // Always update React state when not dragging
      if (!isDragging) {
        setCurrentDate(newDate);
      }

      // Handle data updates with throttling during drag
      const now = Date.now();

      if (isDragging) {
        // During drag: throttle data updates to every 100ms
        if (now - lastUpdateRef.current >= 100) {
          lastUpdateRef.current = now;
          setCurrentDate(newDate);

          // Clear any pending timeout
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }

          const snapshot = calculateSnapshot(newDate);
          onTimeChangeRef.current?.(snapshot);
        }
      } else {
        // When not dragging: use debounced updates for smooth interaction
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
          const snapshot = calculateSnapshot(newDate);
          onTimeChangeRef.current?.(snapshot);
        }, 25);
      }
    },
    [isDragging, calculateSnapshot, updateCursorPosition]
  );

  // Handle navigating directly to an event's date
  const handleEventNavigation = useCallback(
    (eventDate: Date) => {
      setCurrentDate(eventDate);

      // Clear any pending debounced calls and immediately call parent callback
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      const snapshot = calculateSnapshot(eventDate);
      onTimeChangeRef.current?.(snapshot);

      // Timeline clicks only navigate to date, don't open edit dialog
    },
    [calculateSnapshot]
  );

  // Mouse handlers for timeline scrubbing
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      setIsDragging(true);
      handleTimelineInteraction(e.clientX);
    },
    [handleTimelineInteraction]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isDragging) {
        e.preventDefault(); // Prevent text selection during drag
        handleTimelineInteraction(e.clientX);
      }
    },
    [isDragging, handleTimelineInteraction]
  );

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);

      // Use stored drag date for final sync or fallback to cursor position
      let finalDate = dragDateRef.current;

      if (!finalDate && cursorRef.current) {
        const left = cursorRef.current.style.left;
        const progress = parseFloat(left.replace("%", "")) / 100;
        const timeRange =
          visibleDateRange.endDate.getTime() -
          visibleDateRange.startDate.getTime();
        finalDate = new Date(
          visibleDateRange.startDate.getTime() + progress * timeRange
        );
      }

      if (finalDate) {
        setCurrentDate(finalDate);

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        const snapshot = calculateSnapshot(finalDate);
        onTimeChangeRef.current?.(snapshot);
        // Update breakdown charts snapshot when dragging stops
        setBreakdownSnapshot(snapshot);
      }

      // Reset drag state
      dragDateRef.current = null;
      lastUpdateRef.current = 0;
    }
  }, [isDragging, calculateSnapshot, visibleDateRange]);

  // Global mouse move handler for smooth dragging
  const handleGlobalMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        e.preventDefault();
        handleTimelineInteraction(e.clientX);
      }
    },
    [isDragging, handleTimelineInteraction]
  );

  // Global mouse up handler
  const handleGlobalMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);

      // Use stored drag date for final sync or fallback to cursor position
      let finalDate = dragDateRef.current;

      if (!finalDate && cursorRef.current) {
        const left = cursorRef.current.style.left;
        const progress = parseFloat(left.replace("%", "")) / 100;
        const timeRange =
          visibleDateRange.endDate.getTime() -
          visibleDateRange.startDate.getTime();
        finalDate = new Date(
          visibleDateRange.startDate.getTime() + progress * timeRange
        );
      }

      if (finalDate) {
        setCurrentDate(finalDate);

        // Clear any pending debounced calls and immediately call parent callback
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        const snapshot = calculateSnapshot(finalDate);
        onTimeChangeRef.current?.(snapshot);
        // Update breakdown charts snapshot when dragging stops
        setBreakdownSnapshot(snapshot);
      }

      // Reset drag state
      dragDateRef.current = null;
      lastUpdateRef.current = 0;
    }
  }, [isDragging, calculateSnapshot, visibleDateRange]);

  // Add global event listeners for smooth dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleGlobalMouseMove);
        document.removeEventListener("mouseup", handleGlobalMouseUp);
      };
    }
  }, [isDragging, handleGlobalMouseMove, handleGlobalMouseUp]);

  // Initialize with current date - only run when data structure actually changes
  const currentSnapshot = useMemo(() => {
    return calculateSnapshot(currentDate);
  }, [calculateSnapshot, currentDate]);

  // Allow parent views to refresh when chart data changes at the same scrub date
  useEffect(() => {
    lastNotifiedSnapshotKeyRef.current = null;
  }, [data]);

  // Notify parent of snapshot changes (only when not dragging to prevent infinite loops)
  useEffect(() => {
    if (!isDragging && currentSnapshot) {
      const snapshotKey = getSnapshotNotificationKey(currentSnapshot);
      if (lastNotifiedSnapshotKeyRef.current === snapshotKey) {
        return;
      }
      lastNotifiedSnapshotKeyRef.current = snapshotKey;
      onTimeChangeRef.current?.(currentSnapshot);
    }
  }, [currentSnapshot, isDragging]);

  // Ensure initial snapshot is sent to parent on mount
  useEffect(() => {
    if (currentSnapshot) {
      onTimeChangeRef.current?.(currentSnapshot);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on mount

  // Update breakdown snapshot when not dragging
  useEffect(() => {
    if (!isDragging && currentSnapshot) {
      setBreakdownSnapshot(currentSnapshot);
    }
  }, [currentSnapshot, isDragging]);

  // Group expenses by category (memoized for performance)
  const expensesByCategory = useMemo(() => {
    if (!currentSnapshot?.activeExpenses) return {};

    // Start with regular expenses (projection CAC is a single virtual line item)
    const regularExpenses = currentSnapshot.activeExpenses
      .filter(
        (expense) =>
          !shouldExcludeAcquisitionExpenseFromBurn(
            expense,
            data.customerProjectionData,
          ),
      )
      .reduce(
      (groups, expense) => {
        const category = expense.category;
        if (!groups[category]) {
          groups[category] = [];
        }
        groups[category].push(expense);
        return groups;
      },
      {} as Record<string, ExpenseData[]>
    );

    // Add depreciating assets as expenses
    const targetMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1
    );
    const depreciatingAssets = resolveActiveAssetEvents(data.events, targetMonth)
      .filter(
        (event) =>
          event.monthlyAppreciationRate &&
          event.monthlyAppreciationRate < 0
      )
      .map((asset) => {
        const monthlyImpact = calculateAssetMonthlyImpact(asset, targetMonth);
        if (monthlyImpact < 0) {
          return {
            id: `asset-${asset.id}`,
            name: asset.description,
            category: "Asset Depreciation",
            monthlyAmount: Math.abs(monthlyImpact), // Convert to positive for expense display
            startDate: asset.date,
            color: asset.color || CHART_COLORS.expense,
            // Add custom properties to identify as asset
            isAsset: true,
            assetType: "depreciation",
          } as ExpenseData & { isAsset?: boolean; assetType?: string };
        }
        return null;
      })
      .filter(Boolean) as (ExpenseData & {
      isAsset?: boolean;
      assetType?: string;
    })[];

    // Add asset depreciation to expenses
    if (depreciatingAssets.length > 0) {
      regularExpenses["Asset Depreciation"] = depreciatingAssets;
    }

    return regularExpenses;
  }, [
    currentSnapshot?.activeExpenses,
    data.events,
    data.customerProjectionData,
    currentDate,
    calculateAssetMonthlyImpact,
  ]);

  // Helper function to calculate actual expense amount for current month
  const calculateActualExpenseAmount = useCallback(
    (expense: ExpenseData): number => {
      // Handle asset depreciation expenses
      const assetExpense = expense as ExpenseData & {
        isAsset?: boolean;
        assetType?: string;
      };
      if (assetExpense.isAsset && assetExpense.assetType === "depreciation") {
        return expense.monthlyAmount || 0; // Already calculated as positive value
      }

      // One-time expenses count only in the month they occur (matches monthlyBurn)
      if (expense.isOneTime && expense.oneTimeAmount) {
        const expenseStartDate = safeDate(expense.startDate);
        if (currentDate.getTime() < expenseStartDate.getTime()) {
          return 0;
        }
        const expenseMonth = new Date(
          expenseStartDate.getFullYear(),
          expenseStartDate.getMonth(),
          1,
        );
        const currentMonth = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          1,
        );
        return expenseMonth.getTime() === currentMonth.getTime()
          ? expense.oneTimeAmount
          : 0;
      }

      // For recurring expenses, use the existing scaling calculation
      if (!expense.monthlyAmount) return 0;

      // Convert ExpenseData to Expense interface for scaling calculation
      const expenseForCalculation = {
        ...expense,
        id: expense.id,
        name: expense.name,
        // For customer-based expenses, don't pass the stored amount as it should be calculated dynamically
        amount:
          expense.expenseType === "customer_based" ? 0 : expense.monthlyAmount,
        frequency: "monthly" as const,
        category: expense.category,
        startDate: expense.startDate,
        endDate: expense.endDate,
        isRecurring: true,
        startupId: "", // Not needed for calculation
        expenseType: expense.expenseType || "fixed",
        growthRate: expense.growthRate,
        revenueRatio: expense.revenueRatio,
        // Customer-based expense fields
        customerBaseCostPerCustomer: expense.customerBaseCostPerCustomer,
        customerBaseAmount: expense.customerBaseAmount,
        // Customer acquisition cost fields
        isCustomerAcquisitionCost: expense.isCustomerAcquisitionCost,
        customerAcquisitionCostId: expense.customerAcquisitionCostId,
        customerGrowthModelId: expense.customerGrowthModelId,
        acquisitionCostPerCustomer: expense.acquisitionCostPerCustomer,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      // Calculate total monthly revenue for percentage-based expenses (temporarily set to 0)
      const totalMonthlyRevenue = 0;

      // Calculate customer metrics for this month if customer projection data is available
      let customerData: any = {
        totalCustomers: 0,
        customerCount: 0,
        averageRevenuePerCustomer: 0,
        newCustomersThisMonth: 0,
        growthRate: 0,
        churnRate: 0,
        totalMonthlyRevenue: 0,
        customersByTier: {},
      };

      if (data.customerProjectionData) {
        const currentMonth = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          1
        );
        const actualCustomerMetrics = calculateMonthlyCustomerMetrics(
          currentMonth,
          data.customerProjectionData
        );
        customerData = {
          ...customerData,
          totalCustomers: actualCustomerMetrics.totalCustomers,
          customerCount: actualCustomerMetrics.totalCustomers,
          averageRevenuePerCustomer:
            actualCustomerMetrics.averageRevenuePerCustomer,
          newCustomersThisMonth: actualCustomerMetrics.newCustomers,
          customersByTier: actualCustomerMetrics.customersByTier,
        };
      }

      return calculateExpenseAtMonth(
        expenseForCalculation,
        currentDate,
        totalMonthlyRevenue,
        customerData
      );
    },
    [currentDate]
  );

  // Helper function to calculate actual revenue amount for current month
  const calculateActualRevenueAmount = useCallback(
    (revenue: RevenueData): number => {
      // Handle asset appreciation revenues
      const assetRevenue = revenue as RevenueData & {
        isAsset?: boolean;
        assetType?: string;
      };
      if (assetRevenue.isAsset && assetRevenue.assetType === "appreciation") {
        return revenue.monthlyAmount || 0; // Already calculated amount
      }

      // Convert RevenueData to Revenue interface for scaling calculation
      const revenueForCalculation = {
        ...revenue,
        id: revenue.id,
        name: revenue.name,
        amount: revenue.monthlyAmount,
        type: revenue.category as
          | "funding"
          | "sales"
          | "investment"
          | "grant"
          | "other",
        date: revenue.startDate,
        description: "",
        startupId: "", // Not needed for calculation
        growthRate: revenue.growthRate,
        isRecurring: revenue.isRecurring,
        frequency:
          revenue.frequency === "one-time" ? "monthly" : revenue.frequency,
        startDate: revenue.startDate,
        endDate: revenue.endDate,
        // Customer-driven revenue fields
        isCustomerDriven: revenue.isCustomerDriven,
        customerGrowthModelId: revenue.customerGrowthModelId,
        subscriptionTierIds: revenue.subscriptionTierIds,
        customerCountMultiplier: revenue.customerCountMultiplier,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      // Calculate customer metrics for this month (temporarily disabled)
      const customerData = {
        customerCount: 0,
        averageRevenuePerCustomer: 0,
        newCustomersThisMonth: 0,
        growthRate: 0,
        churnRate: 0,
        totalMonthlyRevenue: 0,
        customersByTier: {},
      };

      return calculateRevenueAtMonth(
        revenueForCalculation,
        currentDate,
        customerData
      );
    },
    [currentDate]
  );

  // Group revenues by category (memoized for performance)
  const revenuesByCategory = useMemo(() => {
    if (!currentSnapshot?.activeRevenues) return {};

    // Start with regular revenues
    const regularRevenues = currentSnapshot.activeRevenues.reduce(
      (groups, revenue) => {
        const category = revenue.category;
        if (!groups[category]) {
          groups[category] = [];
        }
        groups[category].push(revenue);
        return groups;
      },
      {} as Record<string, RevenueData[]>
    );

    // Add appreciating assets as revenue
    const targetMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1
    );
    const appreciatingAssets = resolveActiveAssetEvents(data.events, targetMonth)
      .filter(
        (event) =>
          event.monthlyAppreciationRate &&
          event.monthlyAppreciationRate > 0
      )
      .map((asset) => {
        const monthlyImpact = calculateAssetMonthlyImpact(asset, targetMonth);
        if (monthlyImpact > 0) {
          return {
            id: `asset-${asset.id}`,
            name: asset.description,
            category: "other", // Use valid category but we'll display as "Asset Appreciation"
            monthlyAmount: monthlyImpact,
            startDate: asset.date,
            isRecurring: true,
            frequency: "monthly" as const,
            color: asset.color || "#10b981",
            // Add custom property to identify as asset
            isAsset: true,
            assetType: "appreciation",
          } as RevenueData & { isAsset?: boolean; assetType?: string };
        }
        return null;
      })
      .filter(Boolean) as RevenueData[];

    // Add asset appreciation to revenues
    if (appreciatingAssets.length > 0) {
      regularRevenues["Asset Appreciation"] = appreciatingAssets;
    }

    // Add customer projection revenue if available
    if (data.customerProjectionData) {
      const targetMonth = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        1
      );
      const customerMetrics = calculateMonthlyCustomerMetrics(
        targetMonth,
        data.customerProjectionData
      );

      if (customerMetrics.subscriptionRevenue > 0) {
        const customerRevenueEntry = {
          id: "customer-subscription-revenue",
          name: "Subscription Revenue",
          category: "sales",
          monthlyAmount: customerMetrics.subscriptionRevenue,
          startDate: currentDate,
          isRecurring: true,
          frequency: "monthly" as const,
          color: "#059669",
          isCustomerProjection: true,
        } as RevenueData & { isCustomerProjection?: boolean };

        if (!regularRevenues["Subscriptions"]) {
          regularRevenues["Subscriptions"] = [];
        }
        regularRevenues["Subscriptions"].push(customerRevenueEntry);
      }
    }

    return regularRevenues;
  }, [
    currentSnapshot?.activeRevenues,
    data.events,
    currentDate,
    calculateAssetMonthlyImpact,
    data.customerProjectionData,
  ]);

  const expenseCategories = useMemo(
    () => Object.keys(expensesByCategory),
    [expensesByCategory]
  );

  const maxExpenseCategoryTotal = useMemo(
    () =>
      Math.max(
        ...expenseCategories.map((cat) =>
          expensesByCategory[cat].reduce(
            (sum, exp) => sum + calculateActualExpenseAmount(exp),
            0
          )
        ),
        1
      ),
    [expenseCategories, expensesByCategory, calculateActualExpenseAmount]
  );

  // Timeline progress calculation (relative to visible range)
  const timeProgress =
    visibleDateRange.startDate && visibleDateRange.endDate
      ? (currentDate.getTime() - visibleDateRange.startDate.getTime()) /
        (visibleDateRange.endDate.getTime() -
          visibleDateRange.startDate.getTime())
      : 0;

  // Memoized event markers for performance
  const eventMarkers = useMemo(() => {
    const priorityColorMap = {
      high: CHART_COLORS.expense,
      medium: "#f59e0b",
      low: "#10b981",
    };

    return data.events
      .map((event) => ({
        ...event,
        progress:
          (event.date.getTime() - visibleDateRange.startDate.getTime()) /
          (visibleDateRange.endDate.getTime() -
            visibleDateRange.startDate.getTime()),
        // Use custom color if provided, otherwise fall back to priority color
        color: event.color || priorityColorMap[event.priority],
      }))
      .filter((event) => event.progress >= 0 && event.progress <= 1); // Only show events in visible range
  }, [data.events, visibleDateRange.startDate, visibleDateRange.endDate]);

  // Shared financial cache input for zero-cash calculations
  const financialCacheInput = useMemo(
    () => mapChartDataToStartup(data),
    [data],
  );

  const financialCache = useMemo(
    () => getOrBuildCache(financialCacheInput),
    [financialCacheInput],
  );

  const timelineDurationMs =
    visibleDateRange.endDate.getTime() - visibleDateRange.startDate.getTime();

  const zeroCashDayMarker = useMemo(() => {
    const primary = getPrimaryCashZeroInRange(
      financialCache,
      visibleDateRange.startDate,
      visibleDateRange.endDate,
      currentDate,
      currentSnapshot?.cashBalance,
    );
    if (!primary || timelineDurationMs <= 0) return null;

    const progress =
      (primary.date.getTime() - visibleDateRange.startDate.getTime()) /
      timelineDurationMs;

    if (progress < 0 || progress > 1) return null;

    return {
      date: primary.date,
      progress,
      label: formatCashZeroDate(primary.date),
    };
  }, [
    financialCache,
    visibleDateRange.startDate,
    visibleDateRange.endDate,
    currentDate,
    currentSnapshot?.cashBalance,
    timelineDurationMs,
  ]);

  const projectEndDayMarker = useMemo(() => {
    if (!data.projectEndDate || timelineDurationMs <= 0) return null;

    const progress = getDateProgressInVisibleRange(
      data.projectEndDate,
      visibleDateRange.startDate,
      visibleDateRange.endDate,
    );
    if (progress === null) return null;

    return {
      date: data.projectEndDate,
      progress,
    };
  }, [
    data.projectEndDate,
    visibleDateRange.startDate,
    visibleDateRange.endDate,
    timelineDurationMs,
  ]);

  const cashZeroEvent = useMemo(() => {
    const event = getCashZeroEventForDisplay(
      financialCache,
      currentDate,
      currentSnapshot?.cashBalance,
    );
    if (!event) return null;

    return {
      date: event.date,
      isUpcoming: event.date.getTime() > currentDate.getTime(),
    };
  }, [financialCache, currentDate, currentSnapshot?.cashBalance]);

  // Secondary cash transition markers (after funding recovery, etc.)
  const cashTransitionMarkers = useMemo(() => {
    const transitions = getCashTransitions(
      financialCache,
      visibleDateRange.startDate,
      visibleDateRange.endDate,
    );

    const primaryDate = zeroCashDayMarker?.date.getTime();

    return transitions
      .filter(
        (transition) =>
          primaryDate === undefined ||
          transition.date.getTime() !== primaryDate,
      )
      .map((transition, index) => ({
        id: `cash-transition-${index}`,
        date: transition.date,
        type: transition.type,
        balance: transition.balance,
        progress:
          (transition.date.getTime() - visibleDateRange.startDate.getTime()) /
          timelineDurationMs,
        color: transition.type === "zero" ? CHART_COLORS.warn : CHART_COLORS.expense,
        description:
          transition.type === "zero"
            ? "Cash balance reaches zero"
            : "Cash balance goes negative",
      }))
      .filter(
        (transition) => transition.progress >= 0 && transition.progress <= 1,
      );
  }, [
    financialCache,
    visibleDateRange.startDate,
    visibleDateRange.endDate,
    zeroCashDayMarker,
    timelineDurationMs,
  ]);

  // Memoized financial event markers for performance
  const financialEventMarkers = useMemo(() => {
    const markers: Array<{
      id: string;
      date: Date;
      type:
        | "expense-added"
        | "expense-removed"
        | "revenue-added"
        | "revenue-removed"
        | "one-time-expense";
      name: string;
      amount: number;
      category: string;
      progress: number;
      color: string;
      description: string;
      isOneTime?: boolean;
    }> = [];

    // Add expense start markers
    data.expenses.forEach((expense) => {
      const startProgress =
        (expense.startDate.getTime() - visibleDateRange.startDate.getTime()) /
        (visibleDateRange.endDate.getTime() -
          visibleDateRange.startDate.getTime());

      if (startProgress >= 0 && startProgress <= 1) {
        if (expense.isOneTime && expense.oneTimeAmount) {
          // One-time expense marker
          markers.push({
            id: `one-time-expense-${expense.id}`,
            date: expense.startDate,
            type: "one-time-expense",
            name: expense.name,
            amount: expense.oneTimeAmount,
            category: expense.category,
            progress: startProgress,
            color: CHART_COLORS.expenseMuted,
            description: `One-time expense: ${
              expense.name
            } ($${expense.oneTimeAmount.toLocaleString()})`,
            isOneTime: true,
          });
        } else if (expense.monthlyAmount) {
          // Recurring expense marker
          markers.push({
            id: `expense-start-${expense.id}`,
            date: expense.startDate,
            type: "expense-added",
            name: expense.name,
            amount: expense.monthlyAmount,
            category: expense.category,
            progress: startProgress,
            color: CHART_COLORS.expense,
            description: `Added expense: ${
              expense.name
            } ($${expense.monthlyAmount.toLocaleString()}/mo)`,
          });
        }
      }

      // Add expense end markers if endDate exists (only for recurring expenses)
      if (expense.endDate && !expense.isOneTime && expense.monthlyAmount) {
        const endProgress =
          (expense.endDate.getTime() - visibleDateRange.startDate.getTime()) /
          (visibleDateRange.endDate.getTime() -
            visibleDateRange.startDate.getTime());

        if (endProgress >= 0 && endProgress <= 1) {
          markers.push({
            id: `expense-end-${expense.id}`,
            date: expense.endDate,
            type: "expense-removed",
            name: expense.name,
            amount: expense.monthlyAmount,
            category: expense.category,
            progress: endProgress,
            color: "#fbbf24", // Amber for expense removal
            description: `Removed expense: ${
              expense.name
            } ($${expense.monthlyAmount.toLocaleString()}/mo)`,
          });
        }
      }
    });

    // Add revenue start markers
    if (data.revenues) {
      data.revenues.forEach((revenue) => {
        const startProgress =
          (revenue.startDate.getTime() - visibleDateRange.startDate.getTime()) /
          (visibleDateRange.endDate.getTime() -
            visibleDateRange.startDate.getTime());

        if (startProgress >= 0 && startProgress <= 1) {
          markers.push({
            id: `revenue-start-${revenue.id}`,
            date: revenue.startDate,
            type: "revenue-added",
            name: revenue.name,
            amount: revenue.monthlyAmount,
            category: revenue.category,
            progress: startProgress,
            color: "#10b981", // Green for revenues
            description: `Added revenue: ${
              revenue.name
            } ($${revenue.monthlyAmount.toLocaleString()}/mo)`,
          });
        }

        // Add revenue end markers if endDate exists
        if (revenue.endDate) {
          const endProgress =
            (revenue.endDate.getTime() - visibleDateRange.startDate.getTime()) /
            (visibleDateRange.endDate.getTime() -
              visibleDateRange.startDate.getTime());

          if (endProgress >= 0 && endProgress <= 1) {
            markers.push({
              id: `revenue-end-${revenue.id}`,
              date: revenue.endDate,
              type: "revenue-removed",
              name: revenue.name,
              amount: revenue.monthlyAmount,
              category: revenue.category,
              progress: endProgress,
              color: "#f59e0b", // Orange for revenue removal
              description: `Removed revenue: ${
                revenue.name
              } ($${revenue.monthlyAmount.toLocaleString()}/mo)`,
            });
          }
        }
      });
    }

    return markers.sort((a, b) => a.progress - b.progress);
  }, [
    data.expenses,
    data.revenues,
    visibleDateRange.startDate,
    visibleDateRange.endDate,
  ]);

  const revisionMarkers = useMemo(
    () =>
      buildRevisionTimelineMarkers(
        {
          expenses: data.expenses,
          revenues: data.revenues ?? [],
          events: data.events,
        },
        visibleDateRange.startDate,
        visibleDateRange.endDate,
      ),
    [
      data.expenses,
      data.revenues,
      data.events,
      visibleDateRange.startDate,
      visibleDateRange.endDate,
    ],
  );

  const timeSegmentationMarkers = useMemo(
    () =>
      getTimelineSegmentMarkers(
        visibleDateRange.startDate,
        visibleDateRange.endDate,
      ),
    [visibleDateRange.startDate, visibleDateRange.endDate],
  );

  // Determine if current date is past, present, or future
  const today = new Date();
  const isCurrentTimePast = currentDate < today;
  const isCurrentTimePresent =
    Math.abs(currentDate.getTime() - today.getTime()) < 24 * 60 * 60 * 1000;
  const isCurrentTimeFuture = currentDate > today;

  // Determine if current date is before startup officially began
  const isPreStartup = currentDate < data.startDate;

  return (
    <div
      ref={containerRef}
      className="flex w-full flex-col overflow-hidden bg-surface"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* Header with current snapshot info */}
      <FinancialHeader
        currentDate={currentDate}
        isPreStartup={isPreStartup}
        isCurrentTimePast={isCurrentTimePast}
        isCurrentTimePresent={isCurrentTimePresent}
        isCurrentTimeFuture={isCurrentTimeFuture}
        data={{
          initialBalance: data.initialBalance,
          startDate: data.startDate,
        }}
        currentSnapshot={currentSnapshot}
        customerProjectionData={data.customerProjectionData}
        cashZeroEvent={cashZeroEvent}
        onHelpModalOpen={() => setIsHelpModalOpen(true)}
      />

      {/* Main visualization area */}
      <div className="flex flex-1">
        {/* Expense categories on Y-axis */}
        <aside className="sh-sidebar w-1/4 min-w-72 max-w-sm overflow-y-auto lg:max-w-md">
          <div className="p-4">
            {isPreStartup ? (
              <PreStartupDisplay
                currentDate={currentDate}
                startDate={data.startDate}
              />
            ) : (
              <>
                <RevenueBreakdownPanel
                  revenuesByCategory={revenuesByCategory}
                  currentSnapshot={currentSnapshot}
                  calculateActualRevenueAmount={calculateActualRevenueAmount}
                  onRevenueClick={onRevenueClick}
                />

                <ExpenseBreakdownPanel
                  expensesByCategory={expensesByCategory}
                  expenseCategories={expenseCategories}
                  currentSnapshot={currentSnapshot}
                  maxExpenseCategoryTotal={maxExpenseCategoryTotal}
                  calculateActualExpenseAmount={calculateActualExpenseAmount}
                  onExpenseClick={onExpenseClick}
                />
              </>
            )}
          </div>
        </aside>

        {/* Timeline scrubber area */}
        <div className="flex min-w-0 flex-1 flex-col bg-surface-secondary/40">
          <TimelineSection
            currentDate={currentDate}
            isDragging={isDragging}
            timeProgress={timeProgress}
            visibleDateRange={visibleDateRange}
            data={data}
            cursorRef={cursorRef}
            timelineRef={timelineRef}
            eventMarkers={eventMarkers}
            cashTransitionMarkers={cashTransitionMarkers}
            zeroCashDayMarker={zeroCashDayMarker}
            projectEndDayMarker={projectEndDayMarker}
            financialEventMarkers={financialEventMarkers}
            revisionMarkers={showRevisionMarkers ? revisionMarkers : []}
            showRevisionMarkers={showRevisionMarkers}
            onToggleRevisionMarkers={() =>
              setShowRevisionMarkers((value) => !value)
            }
            timeSegmentationMarkers={timeSegmentationMarkers}
            onMouseDown={handleMouseDown}
            onEventNavigation={handleEventNavigation}
          />

          <EventsTimeline
            currentDate={currentDate}
            data={data}
            onEventClick={onEventClick}
          />

          {/* Cash flow visualization */}

          <FinancialVisualizationDashboard
            currentSnapshot={currentSnapshot}
            breakdownSnapshot={breakdownSnapshot}
            data={data}
            visibleDateRange={visibleDateRange}
            rawExpenses={rawExpenses}
            rawRevenues={rawRevenues}
            rawEvents={rawEvents}
            customerProjections={customers}
            subscriptionTiers={subscriptionTiers}
            capacityGroups={capacityGroups}
            teamMembers={teamMembers}
            customerGrowthScenarios={customerGrowthModels}
            acquisitionCostModels={customerAcquisitionCosts}
            startupId={startupId}
            onExpenseClick={onExpenseClick}
            onRevenueClick={onRevenueClick}
            onEventClick={onEventClick}
            onCustomerProjectionClick={onCustomerProjectionClick}
            onSubscriptionTierClick={onSubscriptionTierClick}
            onCapacityGroupClick={onCapacityGroupClick}
            onAddExpense={onAddExpense}
            onAddRevenue={onAddRevenue}
            onAddEvent={onAddEvent}
            onIncorporateGrowthPlan={onIncorporateGrowthPlan}
            onAddCustomerProjection={onAddCustomerProjection}
            onAddCapacityGroup={onAddCapacityGroup}
            onRevisionReviseAgain={onRevisionReviseAgain}
            readOnly={readOnly}
          />
        </div>
      </div>

      {/* Help Modal */}
      <FundingCalculatorHelpModal
        isOpen={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
      />
    </div>
  );
};

export default BurndownChart;
